import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import BusinessOutlined from '@mui/icons-material/BusinessOutlined';
import WorkspacePremiumOutlined from '@mui/icons-material/WorkspacePremiumOutlined';
import GroupOutlined from '@mui/icons-material/GroupOutlined';
import LayersOutlined from '@mui/icons-material/LayersOutlined';
import TuneOutlined from '@mui/icons-material/TuneOutlined';
import SpeedOutlined from '@mui/icons-material/SpeedOutlined';
import AutoAwesomeOutlined from '@mui/icons-material/AutoAwesomeOutlined';
import AccountBalanceOutlined from '@mui/icons-material/AccountBalanceOutlined';
import NotificationsNoneOutlined from '@mui/icons-material/NotificationsNoneOutlined';
import LockOutlined from '@mui/icons-material/LockOutlined';
import HistoryOutlined from '@mui/icons-material/HistoryOutlined';
import FileDownloadOutlined from '@mui/icons-material/FileDownloadOutlined';
import SearchOutlined from '@mui/icons-material/SearchOutlined';
import ArrowForwardOutlined from '@mui/icons-material/ArrowForwardOutlined';
import PersonAddAltOutlined from '@mui/icons-material/PersonAddAltOutlined';
import AddBusinessOutlined from '@mui/icons-material/AddBusinessOutlined';
import NotificationsActiveOutlined from '@mui/icons-material/NotificationsActiveOutlined';
import ShieldOutlined from '@mui/icons-material/ShieldOutlined';
import FactCheckOutlined from '@mui/icons-material/FactCheckOutlined';
import ChevronRightOutlined from '@mui/icons-material/ChevronRightOutlined';
import { monitoringQueries, myRiskQueries, settingsQueries } from '../../lib/queries';
import { useAuth } from '../../context/AuthContext';
import { useTheme as useCustomTheme } from '../../context/ThemeContext';
import { sans } from '../Vendors/VendorDetails';
import AIProviders from './AIProviders';
import FinancialProviders from './FinancialProviders';
import {
  DataSection, GeneralSection, HistorySection, NotificationsSection, PlanSection, RiskTieringSection, ScoringSection,
  SecuritySection, TeamSection, WorkflowDefaultsSection, useSetupSteps,
} from './SettingsSections';
import { Badge, ConfirmDialog, Palette, SettingsKeyframes, SettingsSectionProps, Tone, fmtDate, palette, timeAgo } from './settingsUi';

/**
 * Main-sidebar Settings. Fills the content area: section navigation on the left, the section in the middle, and on
 * wide screens a status rail (workspace + plan usage, setup progress, recent changes) on the right. Ctrl/Cmd+K opens
 * a command palette over every section and common actions. Deep-linkable with ?section=; unsaved edits are guarded.
 * Each section is its own component (SettingsSections.tsx, AIProviders.tsx, FinancialProviders.tsx).
 */

type SectionId =
  | 'general' | 'plan' | 'team'
  | 'risk-tiering' | 'workflow' | 'scoring'
  | 'ai-providers' | 'financial' | 'notifications'
  | 'security' | 'history' | 'data';

interface SectionDef {
  id: SectionId; label: string; icon: ReactNode; keywords: string; summary: string; adminOnly?: boolean;
}

const ICON = { fontSize: 'small' as const, sx: { fontSize: 18 } };
const GROUPS: { title: string; items: SectionDef[] }[] = [
  {
    title: 'Organization', items: [
      { id: 'general', label: 'General', icon: <BusinessOutlined {...ICON} />, summary: 'Company name, workspace ID, setup',
        keywords: 'company name workspace id profile setup checklist account' },
      { id: 'plan', label: 'Plan & usage', icon: <WorkspacePremiumOutlined {...ICON} />, summary: 'Subscription, limits and usage',
        keywords: 'billing subscription trial limits vendors users seats' },
      { id: 'team', label: 'Team & roles', icon: <GroupOutlined {...ICON} />, summary: 'Members, admins and permissions',
        keywords: 'users members invite admins permissions rbac viewer analyst' },
    ],
  },
  {
    title: 'Risk program', items: [
      { id: 'risk-tiering', label: 'Risk tiering', icon: <LayersOutlined {...ICON} />, summary: 'Inherent tiers and review cadence',
        keywords: 'inherent tier data sensitivity critical high review cadence cycle pii' },
      { id: 'workflow', label: 'Workflow defaults', icon: <TuneOutlined {...ICON} />, summary: 'Starting values for new work',
        keywords: 'renewal notice remediation due date reminder screening access review interval' },
      { id: 'scoring', label: 'Scoring methodology', icon: <SpeedOutlined {...ICON} />, summary: 'How scores are calculated',
        keywords: 'score bands categories weights formula risk level audit' },
    ],
  },
  {
    title: 'Integrations', items: [
      { id: 'ai-providers', label: 'AI providers', icon: <AutoAwesomeOutlined {...ICON} />, summary: 'Your own LLM keys',
        keywords: 'openai anthropic gemini llm byok model key', adminOnly: true },
      { id: 'financial', label: 'Financial data', icon: <AccountBalanceOutlined {...ICON} />, summary: 'Financial health providers',
        keywords: 'financial health credit provider sync' },
      { id: 'notifications', label: 'Alert delivery', icon: <NotificationsNoneOutlined {...ICON} />, summary: 'Email, Slack, Teams, webhook',
        keywords: 'notifications email slack teams webhook alerts channels' },
    ],
  },
  {
    title: 'Security & data', items: [
      { id: 'security', label: 'Security', icon: <LockOutlined {...ICON} />, summary: 'Sign-in, sessions and protection',
        keywords: 'password 2fa mfa sso saml session rate limit encryption' },
      { id: 'history', label: 'Change history', icon: <HistoryOutlined {...ICON} />, summary: 'Who changed what, when',
        keywords: 'audit log changes who when', adminOnly: true },
      { id: 'data', label: 'Data & exports', icon: <FileDownloadOutlined {...ICON} />, summary: 'Exports and retention',
        keywords: 'export csv pdf retention download' },
    ],
  },
];
const ALL_SECTIONS = GROUPS.flatMap(g => g.items.map(item => ({ ...item, group: g.title })));
const isSectionId = (v: string | null): v is SectionId => !!v && ALL_SECTIONS.some(s => s.id === v);

const PLAN_LABEL: Record<string, string> = { free: 'Free', pro: 'Pro', custom: 'Custom', enterprise: 'Enterprise' };

function useViewportWidth() {
  const [width, setWidth] = useState(() => (typeof window === 'undefined' ? 1440 : window.innerWidth));
  useEffect(() => {
    const onResize = () => setWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return width;
}

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);
const SHORTCUT = isMac ? '⌘K' : 'Ctrl K';

/* ─── Command palette ─────────────────────────────────────────────────────────────────── */

interface PaletteEntry { key: string; label: string; hint: string; group: string; icon: ReactNode; keywords: string; run: () => void }

function CommandPalette({ c, open, entries, onClose }: { c: Palette; open: boolean; entries: PaletteEntry[]; onClose: () => void }) {
  const [q, setQ] = useState('');
  const [index, setIndex] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);
  useEffect(() => { if (open) { setQ(''); setIndex(0); } }, [open]);

  const needle = q.trim().toLowerCase();
  const results = useMemo(() => (needle
    ? entries.filter(e => `${e.label} ${e.hint} ${e.group} ${e.keywords}`.toLowerCase().includes(needle))
    : entries), [entries, needle]);
  useEffect(() => { setIndex(0); }, [needle]);
  useEffect(() => {
    listRef.current?.querySelector(`[data-index="${index}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [index]);

  if (!open) return null;
  const pick = (e?: PaletteEntry) => { if (!e) return; onClose(); e.run(); };
  let lastGroup = '';

  return createPortal(
    <div role="presentation" onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }} style={{
      position: 'fixed', inset: 0, zIndex: 1500, background: c.isDark ? 'rgba(0,0,0,.65)' : 'rgba(15,23,42,.38)',
      display: 'flex', justifyContent: 'center', alignItems: 'flex-start', padding: '12vh 16px 16px', fontFamily: sans,
    }}>
      <div role="dialog" aria-modal="true" aria-label="Search settings" style={{
        width: '100%', maxWidth: 600, background: c.surface, border: `1px solid ${c.line}`, borderRadius: 14, overflow: 'hidden',
        boxShadow: c.isDark ? '0 24px 70px rgba(0,0,0,.85)' : '0 24px 60px rgba(15,23,42,.28)', animation: 'settingsPop .14s ease-out',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 16px', borderBottom: `1px solid ${c.line}` }}>
          <SearchOutlined sx={{ fontSize: 20, color: c.muted }} />
          <input autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder="Search settings and actions…"
            aria-label="Search settings and actions"
            onKeyDown={e => {
              if (e.key === 'ArrowDown') { e.preventDefault(); setIndex(i => Math.min(results.length - 1, i + 1)); }
              if (e.key === 'ArrowUp') { e.preventDefault(); setIndex(i => Math.max(0, i - 1)); }
              if (e.key === 'Enter') { e.preventDefault(); pick(results[index]); }
              if (e.key === 'Escape') { e.preventDefault(); onClose(); }
            }}
            style={{ flex: 1, border: 'none', outline: 'none', background: 'transparent', color: c.ink, fontSize: 15, fontFamily: sans }} />
          <kbd style={kbd(c)}>Esc</kbd>
        </div>
        <div ref={listRef} style={{ maxHeight: 400, overflowY: 'auto', padding: 6 }}>
          {results.length === 0 && (
            <div style={{ padding: '28px 12px', textAlign: 'center', fontSize: 13, color: c.slate }}>No settings match “{q}”.</div>
          )}
          {results.map((e, i) => {
            const header = e.group !== lastGroup ? e.group : null;
            lastGroup = e.group;
            const active = i === index;
            return (
              <div key={e.key}>
                {header && <div style={{ padding: '10px 10px 4px', fontSize: 11, fontWeight: 600, color: c.muted, letterSpacing: '.03em' }}>{header}</div>}
                <button type="button" data-index={i} onMouseEnter={() => setIndex(i)} onClick={() => pick(e)} style={{
                  width: '100%', display: 'flex', alignItems: 'center', gap: 12, padding: '9px 10px', borderRadius: 8, border: 'none',
                  background: active ? (c.isDark ? '#171717' : '#F1F5F9') : 'transparent', cursor: 'pointer', textAlign: 'left', fontFamily: sans,
                }}>
                  <span style={{
                    width: 30, height: 30, borderRadius: 8, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    background: active ? c.accentBg : c.chip, color: active ? c.accent : c.slate,
                  }}>{e.icon}</span>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: 'block', fontSize: 13.5, fontWeight: 600, color: c.ink }}>{e.label}</span>
                    <span style={{ display: 'block', fontSize: 12, color: c.slate, marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{e.hint}</span>
                  </span>
                  {active && <ArrowForwardOutlined sx={{ fontSize: 16, color: c.muted }} />}
                </button>
              </div>
            );
          })}
        </div>
        <div style={{ display: 'flex', gap: 14, padding: '9px 16px', borderTop: `1px solid ${c.line}`, background: c.surface2, fontSize: 11.5, color: c.muted }}>
          <span><kbd style={kbd(c)}>↑</kbd> <kbd style={kbd(c)}>↓</kbd> to move</span>
          <span><kbd style={kbd(c)}>Enter</kbd> to open</span>
          <span style={{ marginLeft: 'auto' }}>{SHORTCUT} anywhere in Settings</span>
        </div>
      </div>
    </div>,
    document.body,
  );
}

const kbd = (c: Palette) => ({
  display: 'inline-block', minWidth: 18, padding: '1px 6px', borderRadius: 5, fontSize: 11, fontFamily: sans, fontWeight: 600,
  color: c.slate, background: c.surface, border: `1px solid ${c.line}`, textAlign: 'center' as const, lineHeight: '18px',
});

/* ─── Right rail ──────────────────────────────────────────────────────────────────────── */

function RailCard({ c, title, action, children }: { c: Palette; title: string; action?: ReactNode; children: ReactNode }) {
  return (
    <section style={{ background: c.surface, border: `1px solid ${c.line}`, borderRadius: 12, padding: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 12 }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: c.ink }}>{title}</span>
        {action}
      </div>
      {children}
    </section>
  );
}

function UsageMeter({ c, label, used, max }: { c: Palette; label: string; used: number; max: number }) {
  const unlimited = max == null || max < 0;
  const pct = unlimited ? 0 : Math.min(100, Math.round((used / Math.max(max, 1)) * 100));
  const color = pct >= 100 ? c.danger : pct >= 80 ? c.amber : c.accent;
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: c.slate }}>
        <span>{label}</span>
        <span style={{ color: c.ink, fontWeight: 600 }}>{used}<span style={{ color: c.muted, fontWeight: 500 }}> / {unlimited ? '∞' : max}</span></span>
      </div>
      <div style={{ height: 6, borderRadius: 99, background: c.chip, overflow: 'hidden', marginTop: 6 }}>
        <div style={{ width: `${unlimited ? 4 : pct}%`, height: '100%', background: color, borderRadius: 99, transition: 'width .3s' }} />
      </div>
    </div>
  );
}

function ProgressRing({ c, done, total }: { c: Palette; done: number; total: number }) {
  const size = 44, stroke = 5, r = (size - stroke) / 2, circ = 2 * Math.PI * r;
  const pct = total ? done / total : 0;
  return (
    <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }} aria-hidden>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={c.chip} strokeWidth={stroke} />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={pct === 1 ? c.green : c.accent} strokeWidth={stroke}
          strokeLinecap="round" strokeDasharray={circ} strokeDashoffset={circ * (1 - pct)} style={{ transition: 'stroke-dashoffset .4s' }} />
      </svg>
      <span style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11.5, fontWeight: 700, color: c.ink }}>
        {done}/{total}
      </span>
    </div>
  );
}

function SetupRailCard({ c, vendorCount, teamCount, go }: { c: Palette; vendorCount: number; teamCount: number; go: (s: string) => void }) {
  const { loading, doneCount, requiredCount, next } = useSetupSteps(vendorCount, teamCount, go);
  if (loading) return null;
  const complete = doneCount === requiredCount;
  return (
    <RailCard c={c} title="Workspace setup" action={!complete ? <button type="button" onClick={() => go('general')} style={linkBtn(c)}>View all</button> : undefined}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <ProgressRing c={c} done={doneCount} total={requiredCount} />
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: c.ink }}>{complete ? 'All set' : `${requiredCount - doneCount} step${requiredCount - doneCount === 1 ? '' : 's'} left`}</div>
          <div style={{ fontSize: 12, color: c.slate, marginTop: 2, lineHeight: 1.45 }}>
            {complete ? 'Vendors, team, AI provider and alert delivery are in place.' : `Next: ${next?.label}`}
          </div>
        </div>
      </div>
      {next && (
        <button type="button" onClick={next.action} style={{
          marginTop: 12, width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '8px 12px',
          borderRadius: 8, border: `1px solid ${c.line}`, background: c.surface2, color: c.ink, fontSize: 12.5, fontWeight: 600,
          fontFamily: sans, cursor: 'pointer',
        }}>{next.cta} <ArrowForwardOutlined sx={{ fontSize: 15 }} /></button>
      )}
    </RailCard>
  );
}

function RecentChangesCard({ c, go }: { c: Palette; go: (s: string) => void }) {
  const historyQuery = useQuery(settingsQueries.history('all'));
  const entries = (historyQuery.data?.entries ?? []).slice(0, 5);
  return (
    <RailCard c={c} title="Recent changes" action={<button type="button" onClick={() => go('history')} style={linkBtn(c)}>View all</button>}>
      {historyQuery.isPending ? (
        <div style={{ fontSize: 12, color: c.muted }}>Loading…</div>
      ) : entries.length === 0 ? (
        <div style={{ fontSize: 12, color: c.slate, lineHeight: 1.5 }}>No settings have been changed yet.</div>
      ) : (
        <div style={{ position: 'relative', display: 'grid', gap: 12, paddingLeft: 14 }}>
          <div style={{ position: 'absolute', left: 3, top: 6, bottom: 6, width: 1, background: c.line }} />
          {entries.map(e => (
            <div key={e.id} style={{ position: 'relative', minWidth: 0 }}>
              <span style={{ position: 'absolute', left: -14, top: 5, width: 7, height: 7, borderRadius: '50%', background: c.accent, border: `2px solid ${c.surface}` }} />
              <div style={{ fontSize: 12, color: c.ink, lineHeight: 1.45, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                {e.details || e.action}
              </div>
              <div style={{ fontSize: 11, color: c.muted, marginTop: 2 }}>{e.user_name || e.user_email || 'System'} · {timeAgo(e.created_at)}</div>
            </div>
          ))}
        </div>
      )}
    </RailCard>
  );
}

const linkBtn = (c: Palette) => ({
  border: 'none', background: 'transparent', color: c.accent, fontSize: 12, fontWeight: 600, fontFamily: sans, cursor: 'pointer', padding: 0,
});

/* ─── Page ────────────────────────────────────────────────────────────────────────────── */

export default function Settings() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { mode } = useCustomTheme();
  const isDark = mode === 'dark';
  const c = palette(isDark);
  const isAdmin = !!user && (user.role === 'admin' || user.role === 'super_admin');
  const width = useViewportWidth();
  const narrow = width < 900;
  const showRail = width >= 1360;

  const [params, setParams] = useSearchParams();
  const requested = params.get('section');
  const section: SectionId = isSectionId(requested) ? requested : 'general';

  const [paletteOpen, setPaletteOpen] = useState(false);
  const [toast, setToast] = useState<{ text: string; tone: 'ok' | 'error' } | null>(null);
  const toastTimer = useRef<number | undefined>(undefined);
  const notify = useCallback((text: string, tone: 'ok' | 'error' = 'ok') => {
    setToast({ text, tone });
    window.clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setToast(null), 4200);
  }, []);

  // Unsaved-changes guard: a section reports its dirty state; leaving the section (or the page) asks first.
  const [dirty, setDirtyState] = useState(false);
  const setDirty = useCallback((d: boolean) => setDirtyState(d), []);
  const [pending, setPending] = useState<SectionId | null>(null);
  useEffect(() => {
    if (!dirty) return;
    const handler = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  const go = useCallback((id: string, force = false) => {
    if (!isSectionId(id) || id === section) return;
    if (dirty && !force) { setPending(id); return; }
    setDirtyState(false);
    setParams(prev => { const next = new URLSearchParams(prev); next.set('section', id); return next; }, { replace: false });
    window.scrollTo({ top: 0 });
  }, [dirty, section, setParams]);

  // Ctrl/Cmd+K anywhere on the page, or "/" when not typing, opens the command palette.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      const typing = ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName) || target.isContentEditable;
      if ((e.key === 'k' || e.key === 'K') && (e.metaKey || e.ctrlKey)) { e.preventDefault(); setPaletteOpen(o => !o); }
      else if (e.key === '/' && !typing && !paletteOpen) { e.preventDefault(); setPaletteOpen(true); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [paletteOpen]);

  // Real data behind the nav hints and the rail.
  const overviewQuery = useQuery(settingsQueries.overview());
  const overview = overviewQuery.data;
  // Platform-provided AI (backend Phase 54): when FluxWatch itself provides the tenant's AI key
  // (set only by the admin app), there's nothing for this tenant to configure -- the whole
  // "AI providers" section is removed, not just locked, same as the user asked for.
  const aiProvidedByPlatform = overview?.ai_mode === 'platform';
  const visibleGroups = useMemo(
    () => aiProvidedByPlatform ? GROUPS.map(g => ({ ...g, items: g.items.filter(s => s.id !== 'ai-providers') })) : GROUPS,
    [aiProvidedByPlatform],
  );
  useEffect(() => {
    if (aiProvidedByPlatform && section === 'ai-providers') go('general', true);
  }, [aiProvidedByPlatform, section, go]);
  const aiQuery = useQuery({ ...settingsQueries.aiProviders(), enabled: isAdmin && !aiProvidedByPlatform });
  const channelsQuery = useQuery(monitoringQueries.channels());
  const tieringQuery = useQuery(settingsQueries.riskTiering());
  const channelOn = (channelsQuery.data ?? []).some((ch: any) => { try { return !!JSON.parse(ch.config).enabled; } catch { return false; } });

  const hint = (id: SectionId): { text: string; tone: Tone } | null => {
    if (id === 'team' && (overview?.team.pending ?? 0) > 0) return { text: `${overview!.team.pending} pending`, tone: 'warn' };
    if (id === 'plan' && overview?.plan.plan_status === 'trial') {
      const d = overview.plan.trial_days_remaining;
      return { text: d > 0 ? `${d}d left` : 'Ended', tone: d > 7 ? 'info' : 'warn' };
    }
    if (id === 'ai-providers' && isAdmin && aiQuery.isSuccess && (aiQuery.data?.length ?? 0) === 0) return { text: 'Not set', tone: 'muted' };
    if (id === 'notifications' && channelsQuery.isSuccess && !channelOn) return { text: 'Off', tone: 'muted' };
    if (id === 'risk-tiering' && tieringQuery.data?.is_customized) return { text: 'Custom', tone: 'info' };
    return null;
  };

  const current = ALL_SECTIONS.find(s => s.id === section)!;
  const props: SettingsSectionProps = { isDark, isAdmin, notify, setDirty };

  const paletteEntries: PaletteEntry[] = useMemo(() => [
    ...ALL_SECTIONS.filter(s => s.id !== 'ai-providers' || !aiProvidedByPlatform).map(s => ({
      key: `section-${s.id}`, label: s.label, hint: s.summary, group: s.group, icon: s.icon, keywords: s.keywords,
      run: () => go(s.id),
    })),
    ...[
      { key: 'a-invite', label: 'Invite a team member', hint: 'Send someone a sign-in with a role', icon: <PersonAddAltOutlined {...ICON} />, keywords: 'user add member', run: () => navigate('/users/add') },
      { key: 'a-vendor', label: 'Add a vendor', hint: 'Onboard a vendor and start its first scan', icon: <AddBusinessOutlined {...ICON} />, keywords: 'onboard new', run: () => navigate('/vendors/add') },
      { key: 'a-alerting', label: 'Alerting', hint: 'Rules, silences and alert inbox', icon: <NotificationsActiveOutlined {...ICON} />, keywords: 'monitoring rules alerts', run: () => navigate('/monitoring') },
      { key: 'a-org', label: 'My Organization', hint: "Your own company's security posture", icon: <ShieldOutlined {...ICON} />, keywords: 'my risk self company', run: () => navigate('/my-organization') },
      { key: 'a-audit', label: 'Audit logs', hint: 'Every action taken in the workspace', icon: <FactCheckOutlined {...ICON} />, keywords: 'audit log activity', run: () => navigate('/audit-logs') },
    ].map(a => ({ ...a, group: 'Actions' })),
  ], [go, navigate, aiProvidedByPlatform]);

  /* ── nav ── */
  const navItem = (s: SectionDef) => {
    const active = s.id === section;
    const h = hint(s.id);
    const locked = s.adminOnly && !isAdmin;
    return (
      <button key={s.id} type="button" aria-current={active ? 'page' : undefined} onClick={() => go(s.id)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', borderRadius: 8, border: 'none',
          fontSize: 13, fontFamily: sans, fontWeight: active ? 600 : 500, textAlign: 'left', cursor: 'pointer', position: 'relative',
          color: active ? c.ink : c.slate, background: active ? (isDark ? '#171717' : '#EEF2F7') : 'transparent', transition: 'background .12s, color .12s',
        }}
        onMouseEnter={e => { if (!active) { e.currentTarget.style.background = c.hover; e.currentTarget.style.color = c.ink; } }}
        onMouseLeave={e => { if (!active) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = c.slate; } }}>
        <span style={{ display: 'inline-flex', color: active ? c.accent : c.muted }}>{s.icon}</span>
        <span style={{ flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.label}</span>
        {locked ? <LockOutlined sx={{ fontSize: 13, color: c.muted }} titleAccess="Admins only" />
          : h ? <Badge c={c} tone={h.tone}>{h.text}</Badge> : null}
      </button>
    );
  };

  const nav = (
    <aside style={{
      width: 256, flexShrink: 0, position: 'sticky', top: 84, alignSelf: 'flex-start', maxHeight: 'calc(100vh - 100px)',
      display: 'flex', flexDirection: 'column', background: c.surface, border: `1px solid ${c.line}`, borderRadius: 12, overflow: 'hidden',
    }}>
      <div style={{ padding: 12, borderBottom: `1px solid ${c.line}` }}>
        <button type="button" onClick={() => setPaletteOpen(true)} style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderRadius: 8, cursor: 'pointer',
          border: `1px solid ${c.line}`, background: c.surface2, color: c.muted, fontSize: 13, fontFamily: sans, textAlign: 'left',
        }}>
          <SearchOutlined sx={{ fontSize: 17 }} />
          <span style={{ flex: 1 }}>Search settings</span>
          <kbd style={kbd(c)}>{SHORTCUT}</kbd>
        </button>
      </div>
      <nav aria-label="Settings sections" style={{ overflowY: 'auto', padding: '10px 8px 12px', display: 'grid', gap: 16 }}>
        {visibleGroups.map(g => (
          <div key={g.title}>
            <div style={{ fontSize: 11, fontWeight: 600, color: c.muted, padding: '0 10px', marginBottom: 4, letterSpacing: '.03em', textTransform: 'uppercase' }}>{g.title}</div>
            <div style={{ display: 'grid', gap: 1 }}>{g.items.map(navItem)}</div>
          </div>
        ))}
      </nav>
    </aside>
  );

  /* ── rail ── */
  const plan = overview?.plan;
  const rail = showRail && overview ? (
    <aside style={{ width: 300, flexShrink: 0, position: 'sticky', top: 84, alignSelf: 'flex-start', display: 'grid', gap: 14 }}>
      <RailCard c={c} title="Workspace" action={<button type="button" onClick={() => go('plan')} style={linkBtn(c)}>Plan details</button>}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
          <WorkspaceLogo c={c} name={overview.company_name} />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 13.5, fontWeight: 700, color: c.ink, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{overview.company_name}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 3, flexWrap: 'wrap' }}>
              <Badge c={c} tone="info">{plan!.plan_name || PLAN_LABEL[plan!.plan] || plan!.plan} plan</Badge>
              {plan!.plan_status === 'trial' && (
                <Badge c={c} tone={plan!.trial_days_remaining > 7 ? 'muted' : 'warn'}>
                  {plan!.trial_days_remaining > 0 ? `Trial · ${plan!.trial_days_remaining}d left` : 'Trial ended'}
                </Badge>
              )}
            </div>
          </div>
        </div>
        <div style={{ display: 'grid', gap: 12 }}>
          <UsageMeter c={c} label="Vendors" used={overview.vendor_count} max={plan!.max_vendors} />
          <UsageMeter c={c} label="Team members" used={overview.team.total} max={plan!.max_users} />
        </div>
        <div style={{ fontSize: 11.5, color: c.muted, marginTop: 12 }}>Created {fmtDate(overview.created_at)}</div>
      </RailCard>
      {isAdmin && <SetupRailCard c={c} vendorCount={overview.vendor_count} teamCount={overview.team.total} go={go} />}
      {isAdmin && <RecentChangesCard c={c} go={go} />}
      {!isAdmin && (
        <RailCard c={c} title="Your access">
          <div style={{ fontSize: 12.5, color: c.slate, lineHeight: 1.5 }}>
            You can view these settings. Changes need an admin{overview.team.admin_members[0] ? ` such as ${overview.team.admin_members[0].name || overview.team.admin_members[0].email}` : ''}.
          </div>
        </RailCard>
      )}
    </aside>
  ) : null;

  return (
    <div style={{ fontFamily: sans, background: c.bg, minHeight: '100%', width: '100%' }}>
      <SettingsKeyframes />
      <div style={{ padding: narrow ? '18px 16px 40px' : '22px 28px 48px', boxSizing: 'border-box', width: '100%' }}>
        {/* header */}
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', marginBottom: 20 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: c.muted, flexWrap: 'wrap' }}>
              <span>Settings</span><ChevronRightOutlined sx={{ fontSize: 15 }} />
              <span>{current.group}</span><ChevronRightOutlined sx={{ fontSize: 15 }} />
              <span style={{ color: c.ink, fontWeight: 600 }}>{current.label}</span>
            </div>
            <h1 style={{ fontSize: 24, fontWeight: 800, color: c.ink, margin: '6px 0 0', letterSpacing: '-.02em' }}>Settings</h1>
            <p style={{ fontSize: 13, color: c.slate, margin: '4px 0 0' }}>
              {isAdmin ? 'Manage your workspace, risk program, integrations and security.' : 'You have view-only access. Ask an admin to change settings.'}
            </p>
          </div>
          {narrow && (
            <button type="button" onClick={() => setPaletteOpen(true)} style={{
              display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 12px', borderRadius: 8, border: `1px solid ${c.line}`,
              background: c.surface, color: c.ink, fontSize: 13, fontFamily: sans, cursor: 'pointer',
            }}><SearchOutlined sx={{ fontSize: 17 }} /> Search</button>
          )}
        </div>

        <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start', flexDirection: narrow ? 'column' : 'row' }}>
          {narrow ? (
            <label style={{ width: '100%' }}>
              <span style={{ display: 'block', fontSize: 12, color: c.slate, marginBottom: 6 }}>Section</span>
              <select value={section} onChange={e => go(e.target.value)} style={{
                width: '100%', padding: '9px 10px', fontSize: 14, fontFamily: sans, color: c.ink, background: c.surface,
                border: `1px solid ${c.line}`, borderRadius: 8,
              }}>
                {visibleGroups.map(g => (
                  <optgroup key={g.title} label={g.title}>
                    {g.items.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
                  </optgroup>
                ))}
              </select>
            </label>
          ) : nav}

          <main key={section} style={{ flex: 1, minWidth: 0, width: '100%', animation: 'settingsFade .16s ease-out' }} aria-label={current.label}>
            {section === 'general' && <GeneralSection {...props} onNavigate={go} />}
            {section === 'plan' && <PlanSection {...props} />}
            {section === 'team' && <TeamSection {...props} />}
            {section === 'risk-tiering' && <RiskTieringSection {...props} />}
            {section === 'workflow' && <WorkflowDefaultsSection {...props} />}
            {section === 'scoring' && <ScoringSection {...props} />}
            {section === 'ai-providers' && !aiProvidedByPlatform && <AIProviders {...props} />}
            {section === 'financial' && <FinancialProviders {...props} />}
            {section === 'notifications' && <NotificationsSection {...props} />}
            {section === 'security' && <SecuritySection {...props} onNavigate={go} />}
            {section === 'history' && <HistorySection {...props} />}
            {section === 'data' && <DataSection {...props} onNavigate={go} />}
          </main>

          {rail}
        </div>
      </div>

      <CommandPalette c={c} open={paletteOpen} entries={paletteEntries} onClose={() => setPaletteOpen(false)} />

      <ConfirmDialog c={c} open={!!pending} title="Discard unsaved changes?" confirmLabel="Discard changes" tone="danger"
        body="You've changed settings in this section without saving. Leaving now throws those changes away."
        onConfirm={() => { const to = pending; setPending(null); if (to) go(to, true); }} onCancel={() => setPending(null)} />

      {toast && (
        <div role="status" aria-live="polite" style={{
          position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', zIndex: 1500, display: 'flex', alignItems: 'center', gap: 10,
          padding: '10px 16px', borderRadius: 10, fontSize: 13, fontFamily: sans, maxWidth: 'calc(100vw - 32px)',
          background: isDark ? '#1f2937' : '#0F172A', color: '#fff', boxShadow: '0 10px 30px rgba(0,0,0,.25)', animation: 'settingsRise .16s ease-out',
        }}>
          <span style={{
            width: 18, height: 18, borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700,
            background: toast.tone === 'ok' ? '#22C55E' : '#EF4444', color: '#fff', flexShrink: 0,
          }}>{toast.tone === 'ok' ? '✓' : '!'}</span>
          {toast.text}
        </div>
      )}
    </div>
  );
}

/* The workspace's company logo (from its own domain: My Organization, else the signed-in user's work email
   domain), falling back to initials when there's no domain or the logo can't load. */
function WorkspaceLogo({ c, name }: { c: ReturnType<typeof palette>; name?: string | null }) {
  const { data } = useQuery(myRiskQueries.status());
  const domain = data?.profile?.domain || data?.suggested_domain || null;
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [domain]);
  const showLogo = !!domain && !failed;
  return (
    <span style={{
      width: 38, height: 38, borderRadius: 10, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      background: showLogo ? '#FFFFFF' : c.accentBg, border: showLogo ? `1px solid ${c.line}` : 'none',
      color: c.accent, fontSize: 14, fontWeight: 800, overflow: 'hidden',
    }}>
      {showLogo
        ? <img src={`https://www.google.com/s2/favicons?domain=${encodeURIComponent(domain!)}&sz=64`} alt=""
            width={24} height={24} style={{ objectFit: 'contain' }} onError={() => setFailed(true)} />
        : (name || '?').split(/\s+/).map(w => w[0]).join('').slice(0, 2).toUpperCase()}
    </span>
  );
}
